/* eslint-disable camelcase */
import { buildParams } from '@/helpers/url';

const DEFAULT_MAX_RATING_SCALE = 5;

const initialState = {
  keyPositions: [],
  readinessData: [
    { id: 99, name: 'Ready now' },
    { id: 1, name: '1 year' },
    { id: 2, name: '2 years' },
    { id: 3, name: '3 years' },
    { id: 4, name: '4 years' },
    { id: 5, name: '5 years' },
  ],
  successionPlansEmployeeView: {
    data: [],
    meta: {},
    info: {},
  },
  organizations: [],
  employees: {
    data: [],
    existingData: [],
    meta: {},
  },
  serviceLength: [
    {
      label: '< 1 year',
      value: 1,
    },
    {
      label: '1 year',
      value: 2,
    },
    {
      label: '2 years',
      value: 3,
    },
    {
      label: '3 years',
      value: 4,
    },
    {
      label: '4 years',
      value: 5,
    },
    {
      label: '>= 5 years',
      value: 6,
    },
  ],
  successionPoolEdit: {},
  allWording: 'all_filtered_not_selected',
  byTemplate: 'talenta',
  byManual: 'manual',
  templates: {
    data: [],
    meta: {},
  },
  ratingScales: [],
  isLoadingFetchRatingScales: false,
};

const mutations = {
  setKeyPositionsData(state, payload) {
    state.keyPositions = payload?.job_positions || [];
  },
  setOrganizationsData(state, payload) {
    state.organizations = payload || [];
  },
  setKeyEmployeesData(state, payload) {
    state.employees.data = payload || [];
  },
  setKeyEmployeesMeta(state, payload) {
    state.employees.meta = payload || {};
  },
  setKeyEmployeesExistingData(state, payload) {
    state.employees.existingData = payload || [];
  },
  setSuccessionPlansEmployeeViewData(state, payload) {
    state.successionPlansEmployeeView.data = payload || [];
  },
  setSuccessionPlansEmployeeViewInfo(state, payload) {
    state.successionPlansEmployeeView.info = payload || {};
  },
  setSuccessionPlansEmployeeViewMeta(state, payload) {
    state.successionPlansEmployeeView.meta = payload || {};
  },
  setSuccessionPoolEdit(state, payload) {
    state.successionPoolEdit = payload || {};
  },
  setTemplatesData(state, payload) {
    state.templates.data = payload?.contents?.data || [];
  },
  setTemplatesMeta(state, payload) {
    state.templates.meta = {
      ...(payload?.contents || {}),
    };
    if (state.templates.meta.data) {
      delete state.templates.meta.data;
    }
  },
  setRatingScales(state, payload) {
    state.ratingScales = payload || [];
  },
  setIsLoadingFetchRatingScales(state, payload) {
    state.isLoadingFetchRatingScales = payload;
  },
};

const getters = {
  keyPositions(state) {
    return state.keyPositions;
  },
  readinessData(state) {
    return state.readinessData;
  },
  successionPlansEmployeeView(state) {
    return state.successionPlansEmployeeView.data;
  },
  successionPlansEmployeeViewInfo(state) {
    return state.successionPlansEmployeeView.info;
  },
  successionPlansEmployeeViewMeta(state) {
    return state.successionPlansEmployeeView.meta;
  },
  organizations(state) {
    return state.organizations;
  },
  employees(state) {
    return state.employees.data;
  },
  employeesExisting(state) {
    return state.employees.existingData;
  },
  employeesMeta(state) {
    return state.employees.meta;
  },
  serviceLength(state) {
    return state.serviceLength;
  },
  successionPoolEdit(state) {
    return state.successionPoolEdit;
  },
  allWording(state) {
    return state.allWording;
  },
  byTemplate(state) {
    return state.byTemplate;
  },
  byManual(state) {
    return state.byManual;
  },
  templatesMeta(state) {
    return state.templates.meta;
  },
  templates(state) {
    return state.templates.data;
  },
  ratingScales(state) {
    return state.ratingScales;
  },
  isLoadingFetchRatingScales(state) {
    return state.isLoadingFetchRatingScales;
  },
  maxRatingScale(state) {
    return state.ratingScales?.length || DEFAULT_MAX_RATING_SCALE;
  },
};

const actions = {
  async getNominationPositions(context, { user_id, detail, withJobLevel }) {
    const path = `${this._vm.$endPoint}/succession-pools/nomination/${user_id}?with_competency_items=${detail ? 1 : 0}&with_job_level=${
      withJobLevel ? 1 : 0
    }`;
    try {
      const { data, response } = await this._vm.$axios.get(path);

      if ((data?.status === 200 || data?.status_code === 200) && data?.data) {
        return data.data;
      }

      throw response?.data?.message || data?.message || 'Failed to get position list';
    } catch (err) {
      throw Error(err);
    }
  },
  async postPromote(context, payload) {
    const path = `${this._vm.$endPoint}/succession-pools/talent/promote`;

    try {
      const { data, response } = await this._vm.$axios.post(path, payload);

      if (data?.status === 200 || data?.status_code === 200) {
        return data;
      }

      throw response?.data?.message || data?.message || 'Failed to promote employee';
    } catch (err) {
      throw Error(err);
    }
  },
  async updateReadiness(context, payload) {
    const path = `${this._vm.$endPoint}/succession-pools/update-talent-readiness/${payload.uuid}`;

    try {
      const { data, response } = await this._vm.$axios.put(path, payload.payload);

      if (data?.status === 200 || data?.status_code === 200) {
        return data;
      }

      throw response?.data?.message || data?.message || 'Failed to update employee readiness';
    } catch (err) {
      throw Error(err);
    }
  },
  async getKeyPositions({ commit }) {
    const url = `${this._vm.$endPoint}/succession-pools/key-position`;
    try {
      const { data } = await this._vm.$axios({
        method: 'get',
        url,
      });
      commit('setKeyPositionsData', data.data);
      return data;
    } catch (error) {
      throw Error(error);
    }
  },
  async getOrganizations({ commit }) {
    const url = `${this._vm.$endPoint}/succession-pools/organization-list?show_all=true`;
    try {
      const { data } = await this._vm.$axios({
        method: 'get',
        url,
      });
      commit('setOrganizationsData', data.data);
      return data;
    } catch (error) {
      throw Error(error);
    }
  },
  async getEmployees({ commit }, payload) {
    try {
      const { data } = await this._vm.$axios({
        method: 'get',
        url: payload.url,
        params: payload.params,
      });
      commit('setKeyEmployeesData', data.data?.users);
      commit('setKeyEmployeesExistingData', data.data?.existing_users);
      commit('setKeyEmployeesMeta', data.meta);
      return data;
    } catch (error) {
      throw Error(error);
    }
  },
  async getSuccessionPoolEdit({ commit }, payload) {
    try {
      const { data } = await this._vm.$axios({
        method: 'get',
        url: `${this._vm.$endPoint}/succession-pools/${payload.id}`,
      });

      if (data?.status !== 200) {
        throw Error;
      }

      commit('setSuccessionPoolEdit', data?.data);
      return data;
    } catch (error) {
      throw Error(error);
    }
  },
  async getIdpList(context, params) {
    try {
      const queries = buildParams(params);
      const url = `${this._vm.$endPoint}/individual-development-plan?`;
      const { data, response } = await this._vm.$axios.get(url + queries);

      if ((data?.status === 200 || data?.status_code === 200) && data?.data) {
        return data;
      }

      throw response?.data?.message || data?.message || 'Failed to fetch individual plan';
    } catch (err) {
      throw Error(err);
    }
  },
  async getCompetencyList(context, params) {
    try {
      const queries = buildParams(params);
      const url = `${this._vm.$endPoint}/competencies?`;
      const { data, response } = await this._vm.$axios.get(url + queries);

      if ((data?.status === 200 || data?.status_code === 200) && data?.data) {
        return data;
      }

      throw response?.data?.message || data?.message || 'Failed to fetch competency items';
    } catch (err) {
      throw Error(err);
    }
  },
  async createCompetency(context, payload) {
    const path = `${this._vm.$endPoint}/competencies`;

    try {
      const { data, response } = await this._vm.$axios.post(path, payload);

      if ((data?.status === 200 || data?.status_code === 200) && data?.data) {
        return { message: 'Competency item successfully created', data: data.data };
      }

      throw response?.data?.errors || response?.data?.message || data?.message || 'Failed to create competency item';
    } catch (err) {
      throw Error(err);
    }
  },
  async getCompetencyDetail(context, payload) {
    const path = `${this._vm.$endPoint}/competencies/${payload}`;
    try {
      const { data, response } = await this._vm.$axios.get(path);

      if ((data?.status === 200 || data?.status_code === 200) && data?.data) {
        return data?.data;
      }

      throw response?.data?.message || data?.message || 'Failed to get competency item';
    } catch (err) {
      throw Error(err);
    }
  },
  async editCompetency(context, payload) {
    const path = `${this._vm.$endPoint}/competencies/${payload.uuid}`;
    try {
      const { data, response } = await this._vm.$axios.put(path, payload.payload);

      if ((data?.status === 200 || data?.status_code === 200) && data?.data) {
        return { message: 'Competency item successfully updated' };
      }

      throw response?.data?.message || data?.message || 'Failed to edit competency item';
    } catch (err) {
      throw Error(err);
    }
  },
  async deleteCompetency(context, uuids) {
    const path = `${this._vm.$endPoint}/competencies/delete`;

    try {
      const { data, response } = await this._vm.$axios.post(path, { uuids });

      if ((data?.status === 200 || data?.status_code === 200) && data?.data) {
        return 'Competency item successfully deleted';
      }

      throw response?.data?.message || data?.message || 'Failed to delete competency item';
    } catch (err) {
      throw Error(err);
    }
  },
  async verifyBulkDeleteCompetency(context, uuids) {
    const path = `${this._vm.$endPoint}/competencies/available-delete-item`;

    try {
      const { data, response } = await this._vm.$axios.post(path, { uuids });

      if ((data?.status === 200 || data?.status_code === 200) && data?.data) {
        return data;
      }

      throw response?.data?.message || data?.message || 'Failed to check delete availability';
    } catch (err) {
      throw Error(err);
    }
  },
  async getDefaultRatings({ commit }, params) {
    try {
      commit('setIsLoadingFetchRatingScales', true);
      const queries = buildParams(params);
      const url = `${this._vm.$endPoint}/rating-scales?`;
      const { data, response } = await this._vm.$axios.get(url + queries);

      if ((data?.status === 200 || data?.status_code === 200) && data?.data) {
        commit('setRatingScales', data.data);
        return data;
      }

      throw response?.data?.message || data?.message || 'Failed to fetch rating scales';
    } catch (err) {
      throw Error(err);
    } finally {
      commit('setIsLoadingFetchRatingScales', false);
    }
  },
  async updateDefaultRatings(context, payload) {
    try {
      const url = `${this._vm.$endPoint}/rating-scales/${payload.uuid}`;
      const { data, response } = await this._vm.$axios.put(url, payload.payload);

      if ((data?.status === 200 || data?.status_code === 200) && data?.data) {
        return data;
      }

      throw response?.data?.message || data?.message || 'Failed to update default rating';
    } catch (err) {
      throw Error(err);
    }
  },
  async getSuccessionPlansEmployeeView({ commit }, payload) {
    try {
      const { data: response } = await this._vm.$axios({
        method: 'get',
        url: `${this._vm.$endPoint}/succession-pools/employee-view`,
        params: payload.params,
      });

      if (response.status !== 200) {
        throw Error;
      }

      commit('setSuccessionPlansEmployeeViewData', response?.data?.successor_talents);
      commit('setSuccessionPlansEmployeeViewInfo', response?.data?.succession_pool);
      commit('setSuccessionPlansEmployeeViewMeta', response?.meta);
      return response;
    } catch (error) {
      throw Error(error);
    }
  },
  async removeFromPool(_, payload) {
    const url = `${this._vm.$endPoint}/succession-pools/remove-talent`;

    try {
      const { data, response } = await this._vm.$axios({
        method: 'post',
        url,
        data: payload.data,
      });

      if ((data?.status === 200 || data?.status_code === 200) && data?.data) {
        return 'Employee removed from pool';
      }

      throw response?.data?.message || data?.message || 'Failed to remove employee from pool';
    } catch (err) {
      throw Error(err);
    }
  },
  async getTemplates(context, payload) {
    const url = `${this._vm.$endPoint}/review-templates/list-template?${buildParams(payload.params)}`;

    try {
      const res = await this._vm.$axios.get(url);

      context.commit('setTemplatesData', res?.data);
      context.commit('setTemplatesMeta', res?.data);

      if (res.data?.status_code === 200) {
        return res.data.contents.review_templates;
      }

      throw Error(res.response?.data?.message || 'Failed to fetch template data');
    } catch (err) {
      if (err instanceof TypeError) throw Error('Failed to fetch template data');

      throw Error(err);
    }
  },
};

export default {
  namespaced: true,
  state: () => ({ ...initialState }),
  mutations,
  getters,
  actions,
};
